import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

const ROLL_MS = 300;

const NumberContainer = styled.div<{ $width?: number }>`
  position: relative;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  min-width: 1ch;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-align: center;
  transition: width 0.1s;
`;

const Sizer = styled.span`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  height: 0;
  white-space: pre;
`;

const AnimatedNumber = styled.div<{
  $direction: 'up' | 'down';
  $type: 'in' | 'out';
  $animate: boolean;
  $durationMs: number;
}>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 20px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $animate, $direction, $type, $durationMs }) =>
    $animate &&
    ($direction === 'up'
      ? $type === 'in'
        ? css`
            animation: slideInUp ${$durationMs}ms forwards;
          `
        : css`
            animation: slideOutUp ${$durationMs}ms forwards;
          `
      : $type === 'in'
        ? css`
            animation: slideInDown ${$durationMs}ms forwards;
          `
        : css`
            animation: slideOutDown ${$durationMs}ms forwards;
          `)}

  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes slideOutUp {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-100%);
      opacity: 0;
    }
  }
  @keyframes slideInDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes slideOutDown {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(100%);
      opacity: 0;
    }
  }
`;

interface RollingNumberProps {
  value: number;
  direction?: 'up' | 'down';
  sizeTo?: number;
  durationMs?: number;
  children?: (value: number) => React.ReactNode;
}

export const RollingNumber: React.FC<RollingNumberProps> = ({
  value,
  direction: fixedDirection,
  sizeTo,
  durationMs = ROLL_MS,
  children = (current) => current,
}) => {
  const [displayed, setDisplayed] = useState(value);
  const [prev, setPrev] = useState<number | null>(null);
  const [changeDirection, setChangeDirection] = useState<'up' | 'down'>('up');
  const direction = fixedDirection ?? changeDirection;
  const [width, setWidth] = useState<number | undefined>(undefined);
  const sizerRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (sizerRef.current) setWidth(sizerRef.current.offsetWidth);
  }, [displayed, prev, sizeTo]);

  useEffect(() => {
    if (value === displayed) return undefined;
    setChangeDirection(value > displayed ? 'up' : 'down');
    setPrev(displayed);
    setDisplayed(value);
  }, [value, displayed]);

  useEffect(() => {
    if (prev === null) return undefined;
    const timeout = setTimeout(() => setPrev(null), durationMs);
    return () => clearTimeout(timeout);
  }, [prev, displayed, durationMs]);

  return (
    <NumberContainer $width={width}>
      <Sizer ref={sizerRef}>{children(sizeTo ?? Math.max(displayed, prev ?? 0))}</Sizer>
      {prev !== null && (
        <AnimatedNumber $direction={direction} $type="out" $animate $durationMs={durationMs} key={`out-${prev}`}>
          {children(prev)}
        </AnimatedNumber>
      )}
      <AnimatedNumber
        $direction={direction}
        $type="in"
        $animate={prev !== null}
        $durationMs={durationMs}
        key={`in-${displayed}`}
      >
        {children(displayed)}
      </AnimatedNumber>
    </NumberContainer>
  );
};
